import React, { useState } from "react";
import api from "../services/api";
import { usePoll } from "../lib/hooks";
import { useConsole } from "../lib/console";
import { SEV_RANK, fmtDateTime, num, timeAgo } from "../lib/format";
import { Badge, Button, Callout, Card, Empty, ErrorState, Kbd, Segmented, SevBadge, SkeletonRows, useToast } from "../components/ui";
import { Icon } from "../components/Icon";
import { PageHeader } from "../components/ConsoleShell";

const DESTINATIONS = [
  { key: "slack_enabled", label: "Slack", env: "SLACK_WEBHOOK_URL", icon: "message" },
  { key: "teams_enabled", label: "Microsoft Teams", env: "TEAMS_WEBHOOK_URL", icon: "message" },
  { key: "pagerduty_enabled", label: "PagerDuty", env: "PAGERDUTY_ROUTING_KEY", icon: "bell" },
];

const ACTIONS = {
  login: "Signed in",
  login_failed: "Sign-in failed",
  login_blocked: "Sign-in locked out",
  portal_login_success: "Demo portal sign-in",
  portal_logout: "Demo portal sign-out",
  account_alert: "Account holder alerted",
  system_start: "Service started",
  ip_blocked: "Source blocked",
  ip_unblocked: "Source released",
  feeds_refresh: "Threat feeds refreshed",
  audit_verify: "Audit chain verified",
  ai_chat: "AI analyst question",
};
const actionLabel = (a) => ACTIONS[a] || String(a || "").replace(/_/g, " ");

const AUDIT_FILTERS = [
  { id: "", label: "All" },
  { id: "login", label: "Sign-ins" },
  { id: "ip_", label: "Source blocks" },
  { id: "ai_chat", label: "AI analyst" },
  { id: "system", label: "System" },
];

const SHORTCUTS = [
  [["Ctrl", "K"], "Open the command palette (⌘K on macOS)"],
  [["Esc"], "Close the open panel or dialog"],
  [["←", "→"], "Move between tabs when a tab list is focused"],
];

function SystemStatus() {
  const s = usePoll("/api/status", { interval: 30000 });
  const d = s.data;
  return (
    <Card
      className="span-7"
      title="Platform health"
      subtitle={d ? `Checked ${timeAgo(d.timestamp)}` : "Subsystem checks from the backend"}
      icon="server"
      actions={
        d && (
          <Badge tone={d.overall_ok ? "ok" : "medium"} dot>
            {d.overall_ok ? "All systems operational" : "Degraded"}
          </Badge>
        )
      }
      flush
    >
      {s.error ? (
        <ErrorState title="Backend unreachable" detail="The API did not answer the health check. Detections and actions are unavailable until it is back." onRetry={s.reload} />
      ) : !d ? (
        <div className="card-body">
          <SkeletonRows rows={6} />
        </div>
      ) : (
        <div className="list">
          {d.systems.map((sys) => (
            <div key={sys.name} className="list-item" style={{ gridTemplateColumns: "auto minmax(0,1fr) auto" }}>
              <span className={`sev-glyph tone-${sys.ok ? "ok" : "medium"}`} aria-hidden="true">
                <Icon name={sys.ok ? "check" : "alert"} size={14} />
              </span>
              <span className="list-title">{sys.name}</span>
              <span className="list-side">
                <span className="mono">{sys.metric}</span>
                <Badge tone={sys.ok ? "ok" : "medium"}>{sys.ok ? "Healthy" : "Attention"}</Badge>
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Appearance() {
  const ctx = useConsole();
  return (
    <Card className="span-5" title="Appearance" icon="palette">
      <div className="col" style={{ gap: 18 }}>
        <div className="field">
          <span className="field-label">Theme</span>
          <Segmented
            label="Theme"
            value={ctx.theme}
            onChange={ctx.setTheme}
            options={[
              { id: "dark", label: "Dark" },
              { id: "light", label: "Light" },
            ]}
          />
          <span className="field-hint">Dark suits a wall display or night shift; light is easier on printed screenshots.</span>
        </div>
        <label className="switch">
          <input type="checkbox" checked={!!ctx.rail} onChange={(e) => ctx.setRail(e.target.checked)} />
          <span className="switch-track" />
          <span>Compact sidebar (icons only)</span>
        </label>
        <div>
          <div className="field-label" style={{ marginBottom: 8 }}>
            Keyboard
          </div>
          <div className="col" style={{ gap: 8 }}>
            {SHORTCUTS.map(([keys, label]) => (
              <div key={label} className="row" style={{ gap: 10 }}>
                <span className="row" style={{ gap: 4, minWidth: 74 }}>
                  {keys.map((k) => (
                    <Kbd key={k}>{k}</Kbd>
                  ))}
                </span>
                <span className="small muted">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

function Notifications() {
  const toast = useToast();
  const w = usePoll("/api/webhooks/status", { interval: 30000 });
  const [busy, setBusy] = useState(false);
  const d = w.data;
  const configured = d ? DESTINATIONS.filter((x) => d[x.key]).length : 0;
  const minSev = String(d?.min_severity || "HIGH").toUpperCase();
  const testFiltered = (SEV_RANK[minSev] ?? 0) > (SEV_RANK.MEDIUM ?? 0);

  const test = async () => {
    setBusy(true);
    try {
      const before = d?.sent || 0;
      const r = await api.post("/api/webhooks/test");
      const after = r.data?.stats?.sent ?? before;
      if (after > before) toast("Test notification sent", "ok", { detail: `Delivered to ${after - before} destination${after - before === 1 ? "" : "s"}.` });
      else
        toast("Nothing was delivered", "medium", {
          detail: testFiltered ? `The test is medium severity, below the ${minSev.toLowerCase()} minimum.` : "Check the destination URLs and the backend logs.",
          duration: 6500,
        });
      w.reload();
    } catch (e) {
      toast("Test failed", "critical", { detail: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="span-7"
      title="Alert notifications"
      subtitle="Push detections to the tools your team already watches"
      icon="bell"
      footer={
        d && (
          <div className="row-between" style={{ width: "100%", gap: 12, flexWrap: "wrap" }}>
            <span className="small muted">
              {!configured
                ? "Add a destination on the server to enable the test."
                : testFiltered
                  ? `The test event is medium severity, so it is filtered while the minimum is ${minSev.toLowerCase()}.`
                  : "Sends one sample medium-severity alert to every configured destination."}
            </span>
            <Button size="sm" icon="send" loading={busy} disabled={!configured} onClick={test}>
              Send test
            </Button>
          </div>
        )
      }
    >
      {w.error ? (
        <ErrorState detail={w.error} onRetry={w.reload} />
      ) : !d ? (
        <SkeletonRows rows={4} />
      ) : (
        <div className="col" style={{ gap: 16 }}>
          <div className="grid cols-3" style={{ gap: 10 }}>
            {DESTINATIONS.map((x) => (
              <div key={x.key} className="card-inset col" style={{ gap: 6 }}>
                <span className="row" style={{ gap: 8 }}>
                  <Icon name={x.icon} size={15} />
                  <strong style={{ fontSize: 13.5 }}>{x.label}</strong>
                </span>
                <Badge tone={d[x.key] ? "ok" : "neutral"} dot>
                  {d[x.key] ? "Connected" : "Not configured"}
                </Badge>
                {!d[x.key] && <span className="tiny dim mono">{x.env}</span>}
              </div>
            ))}
          </div>
          <div className="row row-wrap" style={{ gap: 24 }}>
            <span className="row" style={{ gap: 8 }}>
              <span className="small muted">Minimum severity</span>
              <SevBadge sev={minSev} />
            </span>
            <span className="small muted">
              Sent <strong className="tnum" style={{ color: "var(--text)" }}>{num(d.sent || 0)}</strong>
            </span>
            <span className="small muted" title="Skipped by the 60-per-minute rate limit">
              Rate-limited <strong className="tnum" style={{ color: "var(--text)" }}>{num(d.suppressed || 0)}</strong>
            </span>
          </div>
          {!configured && (
            <Callout tone="info" icon="info" title="No destinations yet">
              Set the environment variables shown above in the server's <span className="mono">.env</span> file and restart the backend. Counts reset when the backend restarts.
            </Callout>
          )}
        </div>
      )}
    </Card>
  );
}

function Session() {
  const ctx = useConsole();
  return (
    <Card className="span-5" title="Session" icon="lock">
      <div className="col" style={{ gap: 14 }}>
        <p className="small muted" style={{ margin: 0 }}>
          You are signed in on this browser. Signing out ends the session on the server and clears it here.
        </p>
        <Callout tone="medium" icon="alert" title="Single shared role">
          Every console user has the same permissions, and multi-factor sign-in is not available yet.
        </Callout>
        <div>
          <Button variant="danger" icon="logout" onClick={ctx.onLogout}>
            Sign out
          </Button>
        </div>
      </div>
    </Card>
  );
}

function AuditLog() {
  const [action, setAction] = useState("");
  const log = usePoll("/api/audit-log", { params: { limit: 100, ...(action ? { action } : {}) }, interval: 30000 });
  const items = log.data?.items;
  return (
    <Card
      className="span-12"
      flush
      title="Audit trail"
      subtitle="Sign-ins, blocks and administrative actions, newest first"
      icon="clipboard"
      actions={
        <>
          <Segmented size="sm" label="Filter audit trail" value={action} onChange={setAction} options={AUDIT_FILTERS} />
          <Button size="sm" variant="ghost" icon="refresh" onClick={log.reload} aria-label="Refresh audit trail" />
        </>
      }
    >
      {log.error ? (
        <ErrorState detail={log.error} onRetry={log.reload} />
      ) : !items ? (
        <div className="card-body">
          <SkeletonRows rows={6} />
        </div>
      ) : !items.length ? (
        <Empty compact icon="clipboard">
          No entries for this filter.
        </Empty>
      ) : (
        <div className="table-wrap table-scroll" style={{ maxHeight: 480 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>User</th>
                <th className="table-hide-sm">Resource</th>
                <th className="table-hide-sm">From</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id}>
                  <td className="nowrap tnum">{fmtDateTime(a.timestamp)}</td>
                  <td>
                    <span className="strong">{actionLabel(a.action)}</span>
                  </td>
                  <td>{a.username || "—"}</td>
                  <td className="table-hide-sm mono small truncate" style={{ maxWidth: 220 }} title={a.resource}>
                    {a.resource || "—"}
                  </td>
                  <td className="table-hide-sm mono small">{a.ip_address || a.ip || "—"}</td>
                  <td>
                    <Badge tone={a.success === false ? "critical" : "ok"} dot>
                      {a.success === false ? "Failed" : "OK"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function Settings() {
  return (
    <>
      <PageHeader eyebrow="Workspace" icon="settings" title="Settings" description="Platform health, notifications, appearance and the audit trail." />
      <div className="grid cols-12">
        <SystemStatus />
        <Appearance />
        <Notifications />
        <Session />
        <AuditLog />
      </div>
    </>
  );
}
